import Typography from '@mui/material/Typography';
import type { TypographyVariant } from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { ElementType, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{
  percentage?: number;
}>()((theme: Theme, { percentage }) => ({
  progressWidth: {
    maxWidth: 130,
    minWidth: 130,
  },
  statusIcon: {
    height: 18,
    width: 18,
    marginRight: 3,
  },
  statusText: {
    whiteSpace: 'noWrap',
    marginTop: '1px',
  },
  statusChip: {
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'noWrap',
    marginTop: 'auto',
    marginBottom: 'auto',
    display: 'inline',
    float: 'left',
    height: 20,
    borderRadius: 6,
    zIndex: 1,
  },
  statusBar: {
    // @ts-expect-error there are 2 pendings in palette now
    backgroundColor: theme.palette.pending.fill,
    float: 'left',
    width: `${percentage ? percentage * 100 : 0}%`,
    height: 26,
    zIndex: 2,
    position: 'relative',
    left: 0,
    display: 'inline',
    borderRadius: '6px 0px 0px 6px',
    '&.fullRadius': {
      borderRadius: '6px 6px 6px 6px',
      width: '100%',
    },
  },
  insideWrapper: {
    whiteSpace: 'noWrap',
    marginTop: 'auto',
    marginBottom: 'auto',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    zIndex: 3,
    left: 0,
    paddingRight: 20,
    float: 'left',
    display: 'flex',
    flexDirection: 'row',
    height: 'inherit',
    padding: '0px 8px 0px 4px',
    '&.noIcon': {
      padding: '0px 8px 0px 8px',
    },
    '&.statusBar': {
      marginTop: -26,
    },
    '&.center': {
      marginLeft: -3,
    },
  },
}));

export const ChipVariantEnum = Object.freeze({
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  INACTIVE: 'INACTIVE',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  UPCOMING: 'UPCOMING',
  ERROR: 'ERROR',
  BUSINESS: 'BUSINESS',
  CATEGORY: 'CATEGORY',
  INFO: 'INFO',
  CRITICAL: 'CRITICAL',
});

//  https://www.figma.com/file/PoHavQNvuKkgQ8Tkny618q/Web---Components?node-id=9%3A15&t=eCQsLAKYLJPEeSig-0
const getColorStyles = (theme: Theme, variant?: string) => {
  const paletteMapping: Record<string, any> = {
    ACTIVE: theme.palette.active,
    PENDING: theme.palette.pending,
    WARNING: theme.palette.warning,
    ERROR: theme.palette.error,
    INACTIVE: theme.palette.inactive,
    SUCCESS: theme.palette.success,
    UPCOMING: theme.palette.upcoming,
    BUSINESS: theme.palette.pending,
    CATEGORY: {
      light: theme.palette.background.default,
      dark: theme.palette.text.primary,
      main: theme.palette.text.primary,
    },
    INFO: theme.palette.info,
    CRITICAL: theme.palette.critical,
  };

  const colors = variant ? paletteMapping[variant] || theme.palette.inactive : theme.palette.inactive;

  return {
    containerStyle: { backgroundColor: colors.light },
    textColor: { color: colors.dark },
    iconColor: { color: colors.main },
  };
};

export interface ChipProps {
  id?: string;
  icon?: ElementType<any>;
  chipStyle?: string;
  children?: ReactNode;
  variant?: string;
  textVariant?: TypographyVariant;
  percentage?: number;
}

const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ chipStyle, children, icon, id, variant, percentage, textVariant, ...other }, ref) => {
    const { classes, cx } = useStyles({ percentage });
    const theme = useTheme<Theme>();

    const IconToRender = icon;
    const showPercentBar = percentage !== undefined && percentage > 0.03;
    const { containerStyle, textColor, iconColor } = getColorStyles(theme, variant);

    return (
      <div
        ref={ref}
        {...other}
        className={cx(classes.statusChip, chipStyle, percentage ? classes.progressWidth : '')}
        style={containerStyle}
      >
        {showPercentBar && (
          <div
            id={`${id}-status-bar`}
            className={cx(classes.statusBar, percentage && percentage >= 0.97 ? 'fullRadius' : '')}
          />
        )}
        <div
          className={cx(
            classes.insideWrapper,
            icon ? '' : 'noIcon',
            showPercentBar && 'statusBar',
            percentage !== undefined && percentage >= 0.1 && 'center',
          )}
        >
          {icon && IconToRender && <IconToRender style={iconColor} className={classes.statusIcon} />}
          <Typography
            id={id}
            style={textColor}
            className={classes.statusText}
            variant={textVariant ? textVariant : 'captionRegular'}
          >
            {children}
          </Typography>
        </div>
      </div>
    );
  },
);

Chip.displayName = 'SChip';

export default Chip;
